import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Upload, FileText, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const DOCUMENT_TYPES = [
  { value: 'resume', label: 'Resume / CV' },
  { value: 'contract', label: 'Employment Contract' },
  { value: 'nda', label: 'NDA' },
  { value: 'government_id', label: 'Government ID' },
  { value: 'birth_certificate', label: 'Birth Certificate' },
  { value: 'diploma', label: 'Diploma / Degree' },
  { value: 'certificate', label: 'Certificate / Training' },
  { value: 'medical', label: 'Medical Records' },
  { value: 'tax_document', label: 'Tax Document' },
  { value: 'other', label: 'Other' },
];

const DocumentUpload = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    document_type: '',
    document_name: '',
    expiry_date: '',
    notes: '',
  });
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }
      setFile(selectedFile);
      // Auto-fill document name if empty
      if (!formData.document_name) {
        setFormData(prev => ({ ...prev, document_name: selectedFile.name.split('.')[0] }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }
    
    if (!formData.document_type) {
      toast.error('Please select a document type');
      return;
    }
    
    if (!formData.document_name) {
      toast.error('Please enter a document name');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('wos_token');
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('employee_id', employeeId);
      uploadData.append('document_type', formData.document_type);
      uploadData.append('document_name', formData.document_name);
      if (formData.expiry_date) {
        uploadData.append('expiry_date', formData.expiry_date);
      }
      if (formData.notes) {
        uploadData.append('notes', formData.notes);
      }

      await axios.post(`${API_URL}/api/hris/documents/upload`, uploadData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Document uploaded successfully');
      navigate(`/app/hris/employees/${employeeId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Upload Document" subtitle="Add a new document to employee profile">
      <div className="max-w-2xl">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="gap-2 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Profile
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Document
            </CardTitle>
            <CardDescription>
              Upload a document to the employee's profile. Supported formats: PDF, DOC, DOCX, JPG, PNG, XLSX.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* File Upload */}
              <div className="space-y-2">
                <Label htmlFor="file">Select File *</Label>
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.txt"
                  />
                  <label htmlFor="file" className="cursor-pointer">
                    {file ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="h-10 w-10 text-primary" />
                        <div className="text-left">
                          <p className="font-medium">{file.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                        <p className="font-medium">Click to select a file</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Maximum file size: 10MB
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Document Type */}
              <div className="space-y-2">
                <Label htmlFor="document_type">Document Type *</Label>
                <Select
                  value={formData.document_type}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, document_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Document Name */}
              <div className="space-y-2">
                <Label htmlFor="document_name">Document Name *</Label>
                <Input
                  id="document_name"
                  value={formData.document_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, document_name: e.target.value }))}
                  placeholder="Enter a descriptive name"
                />
              </div>

              {/* Expiry Date */}
              <div className="space-y-2">
                <Label htmlFor="expiry_date">Expiry Date (optional)</Label>
                <Input
                  type="date"
                  id="expiry_date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  Set if the document has an expiration date (e.g., ID, certificate)
                </p>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any additional notes about this document"
                  rows={3}
                />
              </div>

              {/* Info Box */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Document versioning</p>
                  <p>If a document of the same type already exists, it will be archived and the new document will become the current version.</p>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="gap-2">
                  {loading ? (
                    <>Uploading...</>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload Document
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DocumentUpload;
